import {axiosClient} from '../axios';
import {
  MemberCollectedProductType,
  MemberProductsBulkOperationResponseType,
  MemberProductScoreType,
  MemberProductType,
  Paginated,
  ProductRequestMetadataType,
  ProductRequestType,
  MemberCollectedProductStatusType,
  ValueOfOption,
  MemberCollectedProductMetadataType,
} from '../types';
import {
  getViewKeyHeader,
  convertFiltersToSearchString,
  getPaginatedFiltersMapping,
} from '../utils';

// - Add/Remove product to/from 'watchlist'
export interface SaveMyProductWatchlistPayloadType {
  product: string;
  is_watched: boolean;
}

export const saveMyProductWatchlist = async (
  formData: SaveMyProductWatchlistPayloadType,
): Promise<MemberProductType> => {
  const {data} = await axiosClient.post<MemberProductType>(
    '/my/products/',
    formData,
  );
  return data;
};

export const resetMyProductWatchlist =
  async (): Promise<MemberProductsBulkOperationResponseType> => {
    const {data} =
      await axiosClient.patch<MemberProductsBulkOperationResponseType>(
        '/my/products/reset-watchlist/',
      );
    return data;
  };

// - UPSERT product rating
export interface SaveMyProductRatingPayloadType {
  product: string;
  rating?: MemberProductScoreType;
}

export const saveMyProductRating = async (
  formData: SaveMyProductRatingPayloadType,
): Promise<MemberProductType> => {
  const {data} = await axiosClient.post<MemberProductType>(
    '/my/products/',
    formData,
  );
  return data;
};

// - UPSERT product review
export interface SaveMyProductReviewPayloadType {
  product: string;
  review_title: string;
  rating?: MemberProductScoreType;
  review_description?: string;
  aroma_enjoyment_score?: MemberProductScoreType;
  balance_and_complexity_score?: MemberProductScoreType;
  body_and_mouthfeel_score?: MemberProductScoreType;
  finish_length_score?: MemberProductScoreType;
  taste_enjoyment_score?: MemberProductScoreType;
  aroma_notes?: Array<string>;
  taste_notes?: Array<string>;
  finish_notes?: Array<string>;
}

export const saveMyProductReview = async (
  formData: SaveMyProductReviewPayloadType,
): Promise<MemberProductType> => {
  const {data} = await axiosClient.post<MemberProductType>(
    '/my/products/',
    formData,
  );

  return data;
};

export enum MyProductsListType {
  collection = 'collection',
  watchlist = 'watchlist',
  rated = 'rated',
  reviewed = 'reviewed',
}

interface MyProductsQueryParamsType {
  listType?: MyProductsListType;
  category?: string;
  rating?: number | string;
  pageSize?: number;
  page?: number;
}

export const getPaginatedListOfMyProducts = async (
  filters?: MyProductsQueryParamsType,
  viewKey?: string,
): Promise<Paginated<MemberProductType>> => {
  const {data} = await axiosClient.get<Paginated<MemberProductType>>(
    `my/products/${convertFiltersToSearchString(
      filters,
      getPaginatedFiltersMapping({
        listType: 'list_type',
        category: 'category',
        pageSize: 'page_size',
        page: 'page',
        rating: 'rating',
      }),
    )}`,
    {headers: getViewKeyHeader(viewKey)},
  );
  return data;
};

export const getListOfMyProducts = async (): Promise<MemberProductType[]> => {
  const products: MemberProductType[] = [];
  let page = 1;

  while (page !== null) {
    const {data} = await axiosClient.get<Paginated<MemberProductType>>(
      `my/products/?page_size=100&page=${page}`,
    );
    if (data.results) {
      products.push(...data.results);
    }
    if (data.next !== null) {
      page++;
    } else {
      page = null;
    }
  }

  return products;
};

export const getMyProduct = async (
  productUuid: string,
  viewKey?: string,
): Promise<MemberProductType> => {
  const {data} = await axiosClient.get<MemberProductType>(
    `my/products/${productUuid}/`,
    {headers: getViewKeyHeader(viewKey)},
  );
  return data;
};

interface MyCollectionProductsQueryParamsType {
  page?: number;
  pageSize?: number;
  category?: string;
  listType?: MyProductsListType;
  status?: ValueOfOption<typeof MemberCollectedProductStatusType>;
}

export const getPaginatedListOfMyCollectionProducts = async (
  filters?: MyCollectionProductsQueryParamsType,
  viewKey?: string,
): Promise<Paginated<MemberProductType>> => {
  const {data} = await axiosClient.get<Paginated<MemberProductType>>(
    `my/products/${convertFiltersToSearchString(
      filters,
      getPaginatedFiltersMapping({
        page: 'page',
        pageSize: 'page_size',
        listType: 'list_type',
        category: 'category',
        status: 'status',
      }),
    )}`,
    {headers: getViewKeyHeader(viewKey)},
  );
  return data;
};

export type ProductRequestPayloadType = Omit<
  ProductRequestType,
  'uuid' | 'image' | 'collected_product'
> & {image?: File};

export const requestProduct = async (
  formData: ProductRequestPayloadType,
): Promise<ProductRequestType> => {
  const {data} = await axiosClient.postForm<ProductRequestType>(
    '/my/product-requests/',
    formData,
  );
  return data;
};

export const getRequestProductMetadata =
  async (): Promise<ProductRequestMetadataType> => {
    const {data} = await axiosClient.get<ProductRequestMetadataType>(
      'my/product-requests/metadata/',
    );
    return data;
  };

export type MemberCollectedProductPayloadType = Partial<
  Omit<
    MemberCollectedProductType,
    'uuid' | 'product' | 'sale_terms_accepted_at'
  >
> & {product_id: string; image?: File};

// - Create/Update product to/from 'collection' (collected products)
export const saveMyCollectedProduct = async (
  formData: MemberCollectedProductPayloadType,
): Promise<MemberCollectedProductType> => {
  const {data} = await axiosClient.post<MemberCollectedProductType>(
    '/my/collected-products/',
    formData,
  );
  return data;
};

export const getMyCollectedProduct = async (
  uuid: string,
): Promise<MemberCollectedProductType> => {
  const {data} = await axiosClient.get<MemberCollectedProductType>(
    `my/collected-products/${uuid}/`,
  );
  return data;
};

export type MemberCollectedProductUpdatePayloadType = Partial<
  Omit<MemberCollectedProductType, 'product' | 'image' | 'receipt'>
> & {image?: File; receipt?: File};

export const updateMyCollectedProduct = async (
  formData: MemberCollectedProductUpdatePayloadType,
): Promise<MemberCollectedProductType> => {
  Object.keys(formData).forEach(
    (key) => formData[key] === '' && delete formData[key],
  );
  const {data} = await axiosClient.patchForm<MemberCollectedProductType>(
    `my/collected-products/${formData.uuid}/`,
    formData,
  );
  return data;
};

export const deleteMyCollectedProduct = async (
  uuid: string,
): Promise<MemberProductsBulkOperationResponseType> => {
  const {data} = await axiosClient.delete<null>(
    `my/collected-products/${uuid}/`,
  );
  return data;
};

export const disposeCollectedProducts = async (
  productId: string,
): Promise<MemberProductsBulkOperationResponseType> => {
  const {data} =
    await axiosClient.post<MemberProductsBulkOperationResponseType>(
      `/my/collected-products/dispose/`,
      {product_id: productId},
    );
  return data;
};

interface MyCollectedProductsQueryParamsType {
  page?: number;
  pageSize?: number;
  productId?: string;
  search?: string;
  status?: string;
  category?: string;
  country?: string;
  ordering?: string;
  brand?: string;
  distillery?: string;
  style?: string;
  type?: string;
}

export const getPaginatedListOfMyCollectedProducts = async (
  filters?: MyCollectedProductsQueryParamsType,
  viewKey?: string,
): Promise<Paginated<MemberCollectedProductType>> => {
  const {data} = await axiosClient.get<Paginated<MemberCollectedProductType>>(
    `my/collected-products/${convertFiltersToSearchString(
      filters,
      getPaginatedFiltersMapping({
        page: 'page',
        pageSize: 'page_size',
        productId: 'product_id',
        search: 'search',
        status: 'status',
        category: 'category',
        country: 'country',
        ordering: 'ordering',
        brand: 'brand',
        distillery: 'distillery',
        style: 'style',
        type: 'type',
      }),
    )}`,
    {headers: getViewKeyHeader(viewKey)},
  );
  return data;
};

export const getMyCollectedProductsMetadata =
  async (): Promise<MemberCollectedProductMetadataType> => {
    const {data} = await axiosClient.get<MemberCollectedProductMetadataType>(
      'my/collected-products/metadata/',
    );
    return data;
  };
